import styles from './Checkbox.module.css'
import { Icon } from '@/components/Icon'
import {
  type AriaAttributes,
  type ChangeEventHandler,
  useCallback,
} from 'react'
import { type FormControlProps } from '@/infra/prop-types'
import { Skeleton, useIsSkeleton } from '@/components/Skeleton'
import cx from 'classnames'
import { mergeProps, useFocusRing, useHover } from 'react-aria'

export interface CheckboxProps extends AriaAttributes, FormControlProps {
  /**
   * The id of the checkbox.
   */
  id?: string
  /**
   * The name of the checkbox.
   */
  name?: string
  /**
   * Whether the checkbox is checked.
   */
  checked?: boolean
  /**
   * Whether the checkbox is a skeleton.
   */
  isSkeleton?: boolean
  /**
   * Whether the checkbox is disabled.
   */
  disabled?: boolean
  /**
   * The callback function that is called when the checked state is changed.
   */
  onChange?: (value: boolean) => unknown
  /**
   * Whether the checkbox is read-only.
   */
  readOnly?: boolean
  /**
   * Whether the checkbox is inert.
   */
  inert?: boolean
  /**
   * Whether to add icon-like padding to the checkbox.
   */
  isPadded?: boolean
  /**
   * Externally control the visual focus state of the checkbox.
   */
  isFocused?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A checkbox which can be either checked or unchecked.
 *
 * This component is Skeleton context-aware.
 */
export function Checkbox({
  id,
  name,
  checked,
  onChange,
  isSkeleton,
  disabled,
  invalid: _,
  isPadded,
  isFocused: isFocusedProp,
  testId,
  ...rest
}: CheckboxProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  disabled ||= isSkeleton

  const change: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      onChange?.(e.target.checked)
    },
    [onChange],
  )

  const { focusProps, isFocusVisible, isFocused } = useFocusRing({
    within: true,
  })

  const { hoverProps, isHovered } = useHover({
    isDisabled: disabled,
  })

  const finalIsFocused = isFocusedProp ?? isFocused

  return (
    <div
      className={cx(
        styles.checkbox,
        disabled && styles.disabled,
        finalIsFocused && styles.focused,
        isFocusVisible && styles.focusVisible,
        isHovered && styles.hovered,
        isPadded && styles.padding,
      )}
    >
      <input
        className={styles.input}
        name={name}
        id={id}
        checked={checked}
        onChange={onChange ? change : undefined}
        type="checkbox"
        disabled={disabled}
        data-test={testId}
        {...mergeProps(focusProps, hoverProps, rest)}
      />
      <div className={styles.default}>
        <Icon name="checkbox-default" />
      </div>
      <div className={styles.checked}>
        <Icon name="checkbox-ticked" />
      </div>
      {isSkeleton && (
        <div className={styles.skeleton}>
          <Skeleton
            borderRadius="inherit"
            variant="rect"
            height="var(--box-size)"
            width="var(--box-size)"
            block
          />
        </div>
      )}
    </div>
  )
}
